import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { Box, Typography, Autocomplete, Chip, Tooltip, } from '@mui/material';
import { Search, Info, ExternalLink } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { manageTrackerList, searchSemrushKeywords, fetchClientNegativeKeywords, saveClientNegativeKeywords } from '../../../../redux/affiliateTrackerSlice';
import CommonDrawerLayout from '../../../../common_components/CommonDrawerLayout';
import AppButton from '../../../../common_components/AppButton';
import useToast from '../../../../common_components/Toast/useToast';
import CustomMonthPicker from '../../../../common_components/daterangepicker/CustomMonthPicker';
import { countriesArray } from '../../../../helper/countries';
import { TextInput, Dropdown } from '../../../../common_components/FormInputs';
import TableToolbar from '../../../../common_components/TableToolbar';
import CommonTable from '../../../../common_components/CommonTable';
import ConfirmActionModal from '../../../../common_components/ConfirmActionModal';
import CommonLoader from '../../../../common_components/CommonLoader';
const SearchKeywordDrawer = ({ open, onClose, trackerId, clientId, onCreated }) => {
    const dispatch = useDispatch();
    const toast = useToast();
    const currentListDetails = useSelector((state) => state.affiliateTracker.currentListDetails);
    const [month, setMonth] = useState(null);
    const [country, setCountry] = useState('us');
    const [searchKeyword, setSearchKeyword] = useState('');
    const [excludeKeywords, setExcludeKeywords] = useState([]);
    const [excludeDomains, setExcludeDomains] = useState([]);
    const [loading, setLoading] = useState(false);
    const [adding, setAdding] = useState(false);
    const [keywordIdeas, setKeywordIdeas] = useState([]);
    const [semrushResults, setSemrushResults] = useState([]);
    const [selectedResults, setSelectedResults] = useState(new Set());
    // Warning state: fires at Search click if term already exists in tracker
    const [overrideWarningOpen, setOverrideWarningOpen] = useState(false);
    // Remembered after user confirms warning — passed as overwrite flag when adding
    const [overwriteOnAdd, setOverwriteOnAdd] = useState(false);
    // Track whether we've finished loading saved values (to avoid saving empty arrays on initial load)
    const [savedLoaded, setSavedLoaded] = useState(false);
    useEffect(() => {
        if (open) {
            setSavedLoaded(false);
            if (clientId) {
                dispatch(fetchClientNegativeKeywords(clientId))
                    .unwrap()
                    .then((saved) => {
                    setExcludeKeywords(saved.keyword ?? []);
                    setExcludeDomains(saved.domain ?? []);
                })
                    .catch(() => { })
                    .finally(() => setSavedLoaded(true));
            }
            else {
                setSavedLoaded(true);
            }
        }
        else {
            setMonth(null);
            setCountry('us');
            setSearchKeyword('');
            setExcludeKeywords([]);
            setExcludeDomains([]);
            setKeywordIdeas([]);
            setSemrushResults([]);
            setSelectedResults(new Set());
            setOverrideWarningOpen(false);
            setOverwriteOnAdd(false);
        }
    }, [open]);
    // Auto-save whenever keywords/domains change (after initial load)
    useEffect(() => {
        if (!open || !clientId || !savedLoaded)
            return;
        dispatch(saveClientNegativeKeywords({ clientId, keywords: excludeKeywords, domains: excludeDomains })).catch(() => { });
    }, [excludeKeywords, excludeDomains]);
    const runSearch = async () => {
        setLoading(true);
        try {
            const formattedDate = month !== "ALL_TIME" && month ? `${month.year}-${String(month.month).padStart(2, '0')}` : undefined;
            const res = await dispatch(searchSemrushKeywords({
                keyword: searchKeyword,
                country: country,
                date: formattedDate,
                negativeKeywords: excludeKeywords,
                negativeDomains: excludeDomains
            })).unwrap();
            const ideas = (res?.keyword_idea || []).map((item, idx) => ({
                ...item,
                id: String(idx)
            }));
            setKeywordIdeas(ideas);
            const results = [...(res?.keyword_data || [])];
            results.sort((a, b) => (a.organic_rank || 0) - (b.organic_rank || 0));
            const mappedResults = results.map((item, idx) => ({
                ...item,
                id: String(idx)
            }));
            setSemrushResults(mappedResults);
        }
        catch (err) {
            console.error(err);
            toast.error(typeof err === 'string' ? err : err?.message || 'Failed to search keywords');
        }
        finally {
            setLoading(false);
            setSelectedResults(new Set());
        }
    };
    const handleSearch = async () => {
        if (!searchKeyword.trim()) {
            toast.error('Search Keyword is required.');
            return;
        }
        // Warn if this search term already exists anywhere in the current tracker
        const termExists = currentListDetails.some((r) => !r.is_removed && r.search_term?.trim().toLowerCase() === searchKeyword.trim().toLowerCase());
        if (termExists) {
            setOverrideWarningOpen(true);
            return;
        }
        setOverwriteOnAdd(false);
        await runSearch();
    };
    // User confirmed warning → search proceeds, overwrite flag set for later add
    const handleConfirmWarning = async () => {
        setOverrideWarningOpen(false);
        setOverwriteOnAdd(true);
        await runSearch();
    };
    const handleCancelWarning = () => {
        setOverrideWarningOpen(false);
    };
    const handleAddToOutreach = async () => {
        if (selectedResults.size === 0) {
            toast.error('Please select at least one result.');
            return;
        }
        setAdding(true);
        let currentTrackerId = trackerId;
        try {
            const selectedArray = Array.from(selectedResults).map(i => semrushResults[Number(i)]);
            const payloadData = selectedArray.map(item => ({
                tracker_id: currentTrackerId,
                search_term: item.search_term || searchKeyword,
                domain: item.domain || '',
                article_link: item.url || '',
                article_visitor: Number(item.traffic ?? item.search_volume) || 0,
                rank: Number(item.organic_rank) || 0,
                client_id: clientId || null,
            }));
            const payload = { data: payloadData, overwrite: overwriteOnAdd };
            const res = await dispatch(manageTrackerList({ action: 'create', payload })).unwrap();
            if (res?.tracker_id && !currentTrackerId) {
                currentTrackerId = res.tracker_id;
            }
            if (onCreated && currentTrackerId) {
                onCreated(currentTrackerId);
            }
            toast.success('Added to outreach successfully!');
            onClose();
        }
        catch (err) {
            toast.error(err || 'Failed to add to outreach.');
        }
        finally {
            setAdding(false);
        }
    };
    // Column definitions for Keyword Ideas Table
    const keywordIdeasColumns = [
        { id: 'keyword', label: 'Keyword', align: 'left', sortable: false },
        { id: 'search_volume', label: 'Volume', align: 'left', sortable: false },
        { id: 'kd', label: 'KD', align: 'left', sortable: false },
    ];
    const formatVolume = (val) => {
        const n = Number(val) || 0;
        if (n >= 1000000)
            return (n / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
        if (n >= 1000)
            return (n / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
        return String(n);
    };
    const renderKeywordIdeasCell = (row, column) => {
        let display = row[column.id];
        if (column.id === 'search_volume')
            display = formatVolume(row.search_volume);
        if (column.id === 'kd')
            display = `${Number(row.kd) || 0}%`;
        return (_jsx(Typography, { sx: { fontSize: '14px !important', color: '#344054', textTransform: 'capitalize' }, children: display }));
    };
    // Column definitions for SEMrush Results Table
    const semrushColumns = [
        { id: 'search_term', label: 'Search Term', align: 'left', sortable: false },
        { id: 'domain', label: 'Domain/Article', align: 'left', sortable: false }
    ];
    const renderSemrushCell = (row, column) => {
        if (column.id === 'search_term') {
            return (_jsx(Typography, { sx: { fontSize: '14px !important', color: '#344054', textTransform: 'capitalize' }, children: row.search_term || searchKeyword }));
        }
        if (column.id === 'domain') {
            return (_jsxs("a", { href: row.url, target: "_blank", rel: "noopener noreferrer", style: { color: 'inherit', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '6px' }, children: [row.domain || row.url, _jsx(ExternalLink, { size: 14, color: "#98A2B3" })] }));
        }
        return null;
    };
    return (_jsxs(_Fragment, { children: [_jsx(CommonDrawerLayout, { open: open, onClose: onClose, title: "Search Keyword", headerActions: _jsxs(AppButton, { variant: "contained", onClick: handleAddToOutreach, disabled: adding || selectedResults.size === 0, sx: { bgcolor: '#EE2A7B', color: '#fff', '&:hover': { bgcolor: '#D91F6A' } }, children: [_jsx(Box, { component: "img", src: "/assets/searchkeyword.svg", sx: { width: 16, height: 16, mr: 1, filter: 'brightness(0) invert(1)' } }), adding ? 'Adding...' : 'Add to Outreach'] }), width: "md", children: _jsxs(Box, { sx: { p: 2, display: 'flex', flexDirection: 'column', gap: 2, bgcolor: '#F9FAFB', minHeight: '100%' }, children: [_jsxs(Box, { sx: { bgcolor: '#fff', p: 1, borderRadius: '8px', border: '1px solid #EAECF0', display: 'flex', flexDirection: 'column', gap: 2 }, children: [_jsxs(Box, { sx: { display: 'flex', gap: 1, alignItems: 'center', mb: 1, justifyContent: 'space-between' }, children: [_jsx(Box, { sx: { flex: 1 }, children: _jsx(Typography, { sx: { fontSize: '16px', fontWeight: 600, color: '#EE2A7B' }, children: "Search Keyword" }) }), _jsxs(Box, { sx: { display: 'flex', gap: 1, alignItems: 'center' }, children: [_jsx(CustomMonthPicker, { value: month, onChange: setMonth, placeholder: "Select Month" }), _jsx(Dropdown, { value: country, onChange: (val) => setCountry(val), options: countriesArray, placeholder: "Country", searchable: true, displayValue: _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1 }, children: [_jsx("img", { src: "/assets/countryicon.svg", alt: "" }), _jsxs(Typography, { sx: { fontSize: '13px', color: '#344054', fontWeight: 500 }, children: ["Country : ", countriesArray.find(c => c.value === country)?.label || 'United States'] })] }) })] })] }), _jsxs(Box, { sx: { display: 'flex', gap: 1 }, children: [_jsx(Box, { sx: { flex: 1 }, children: _jsx(TextInput, { fullWidth: true, placeholder: "Search Keyword", value: searchKeyword, onChange: (e) => setSearchKeyword(e.target.value) }) }), _jsxs(AppButton, { variant: "outlined", onClick: handleSearch, disabled: loading, sx: { borderColor: '#D0D5DD', color: '#344054', textTransform: 'none' }, children: [_jsx(Search, { size: 16, style: { marginRight: 6 } }), "Search"] })] }), _jsxs(Box, { sx: { display: 'flex', gap: 1 }, children: [_jsxs(Box, { sx: { flex: 1 }, children: [_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }, children: [_jsx(Typography, { sx: { fontSize: '13px', fontWeight: 500, color: '#344054' }, children: "Exclude Negative Keywords" }), _jsx(Tooltip, { title: "Keywords to exclude", children: _jsx(Info, { size: 14, color: "#98A2B3", style: { cursor: 'pointer' } }) })] }), _jsx(Autocomplete, { multiple: true, freeSolo: true, options: [], value: excludeKeywords, onChange: (_, newValue) => setExcludeKeywords(newValue), renderTags: (value, getTagProps) => value.map((option, index) => {
                                                        const { key, ...tagProps } = getTagProps({ index });
                                                        return (_jsx(Chip, { variant: "outlined", label: option, ...tagProps, size: "small", sx: {
                                                                bgcolor: '#F5F8FF',
                                                                color: '#3E54D3',
                                                                borderColor: '#D1E0FF',
                                                                borderRadius: '4px',
                                                                '& .MuiChip-deleteIcon': { color: '#8DA4FF', fontSize: '14px', '&:hover': { color: '#3E54D3' } }
                                                            } }, key));
                                                    }), renderInput: (params) => (_jsxs(Box, { ref: params.InputProps.ref, sx: { display: 'flex', flexDirection: 'column', gap: 1 }, children: [params.InputProps.startAdornment && (_jsx(Box, { sx: { display: 'flex', flexWrap: 'wrap', gap: 0.5 }, children: params.InputProps.startAdornment })), _jsx(TextInput, { ...params.inputProps, placeholder: excludeKeywords.length === 0 ? "Gift, Week, etc..." : "Type and press enter" })] })) })] }), _jsxs(Box, { sx: { flex: 1 }, children: [_jsx(Typography, { sx: { fontSize: '13px', fontWeight: 500, color: '#344054', mb: 0.5 }, children: "Exclude Domains" }), _jsx(Autocomplete, { multiple: true, freeSolo: true, options: [], value: excludeDomains, onChange: (_, newValue) => setExcludeDomains(newValue), renderTags: (value, getTagProps) => value.map((option, index) => {
                                                        const { key, ...tagProps } = getTagProps({ index });
                                                        return (_jsx(Chip, { variant: "outlined", label: option, ...tagProps, size: "small" }, key));
                                                    }), renderInput: (params) => (_jsxs(Box, { ref: params.InputProps.ref, sx: { display: 'flex', flexDirection: 'column', gap: 1 }, children: [params.InputProps.startAdornment && (_jsx(Box, { sx: { display: 'flex', flexWrap: 'wrap', gap: 0.5 }, children: params.InputProps.startAdornment })), _jsx(TextInput, { ...params.inputProps, placeholder: excludeDomains.length === 0 ? "Exclude Domains" : "Type and press enter" })] })) })] })] })] }), loading ? (_jsx(Box, { sx: { display: 'flex', justifyContent: 'center', alignItems: 'center', py: 10, flex: 1 }, children: _jsx(CommonLoader, { className: "table-loader" }) })) : (_jsxs(_Fragment, { children: [_jsxs(Box, { sx: { bgcolor: '#fff', borderRadius: '8px', border: '1px solid #EAECF0' }, children: [_jsx(Box, { sx: { p: 2, borderBottom: '1px solid #EAECF0' }, children: _jsx(Typography, { sx: { fontSize: '16px', fontWeight: 600, color: '#101828' }, children: "Keyword ideas" }) }), _jsx(CommonTable, { columns: keywordIdeasColumns, rows: keywordIdeas, renderCell: renderKeywordIdeasCell, expandTrigger: "none", maxHeight: "250px", emptyMessage: "No keyword ideas found", stickyHeader: true })] }), _jsxs(Box, { sx: { bgcolor: '#fff', borderRadius: '8px', border: '1px solid #EAECF0' }, children: [_jsx(Box, { sx: { borderBottom: '1px solid #EAECF0' }, children: _jsx(TableToolbar, { title: "SEMrush Results", showSearch: true, showRows: true, showFullScreen: true }) }), _jsx(CommonTable, { columns: semrushColumns, rows: semrushResults, renderCell: renderSemrushCell, expandTrigger: "none", maxHeight: "350px", emptyMessage: "No results found", enableSelection: true, selectedRows: selectedResults, onSelectionChange: setSelectedResults, stickyHeader: true }), _jsxs(Box, { sx: { p: 2, display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #EAECF0' }, children: [_jsxs(Typography, { sx: { fontSize: '14px !important', color: '#667085', fontWeight: 500 }, children: ["Showing ", semrushResults.length, " entries"] }), _jsxs(Typography, { sx: { fontSize: '14px !important', fontWeight: 500, color: '#344054' }, children: ["Total ", selectedResults.size, " Article Selected"] })] })] })] }))] }) }), _jsx(ConfirmActionModal, { open: overrideWarningOpen, onClose: handleCancelWarning, onConfirm: handleConfirmWarning, title: "Warning!", description: "Search term already exists in the Outreach Tracker. Adding it will overwrite old record(s) relevant to the article. Are you sure you want to continue?", confirmText: "Proceed", cancelText: "Cancel", loading: adding, isDeleteIcon: false, isConfirm: false, isWarning: true })] }));
};
export default SearchKeywordDrawer;
